const EVENT_ENDPOINT = '/api/events';

function eventPayload(eventName, carrier, meta = {}) {
  return {
    eventName,
    experimentKey: carrier.dataset.wlaExperiment || 'read-also-box',
    variantKey: carrier.dataset.wlaExperimentVariant || 'early-editorial',
    surface: carrier.dataset.wlaSurface || 'article',
    placement: carrier.dataset.wlaPlacement || carrier.dataset.wlaInternalPlacement || 'read-also',
    pagePath: `${window.location.pathname}${window.location.search}`,
    pageUrl: window.location.href,
    value: meta.value || 0,
    meta
  };
}

function sendReadAlsoEvent(eventName, carrier, meta = {}) {
  if (!carrier) return;
  try {
    const body = JSON.stringify(eventPayload(eventName, carrier, meta));
    if (navigator.sendBeacon) {
      navigator.sendBeacon(EVENT_ENDPOINT, new Blob([body], { type: 'application/json' }));
      return;
    }
    fetch(EVENT_ENDPOINT, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body,
      keepalive: true
    }).catch(() => {});
  } catch {}
}

function initReadAlsoInviewTracking() {
  const boxes = Array.from(document.querySelectorAll('[data-wla-read-also="1"]'));
  if (!boxes.length) return;

  boxes.forEach((box) => {
    sendReadAlsoEvent('experiment_shown', box, {
      source: 'theme',
      linkCount: box.querySelectorAll('[data-wla-read-also-link="1"]').length
    });
  });

  if (!('IntersectionObserver' in window)) return;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting || entry.intersectionRatio < 0.35) return;
        const box = entry.target;
        if (box.dataset.wlaReadAlsoInviewSent === 'true') return;
        box.dataset.wlaReadAlsoInviewSent = 'true';

        const payload = {
          event: 'wla_read_also_inview',
          read_also_id: box.dataset.wlaReadAlsoId || '',
          link_count: box.querySelectorAll('[data-wla-read-also-link="1"]').length,
          link_location: box.dataset.wlaPlacement || 'read-also',
          source_url: window.location.href
        };

        if (typeof window.gtag === 'function') {
          window.gtag('event', 'wla_read_also_inview', {
            read_also_id: payload.read_also_id,
            link_count: payload.link_count,
            link_location: payload.link_location,
            source_url: payload.source_url
          });
        }

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(payload);
        sendReadAlsoEvent('read_also_inview', box, {
          readAlsoId: payload.read_also_id,
          linkCount: payload.link_count,
          ratio: Math.round(entry.intersectionRatio * 100) / 100
        });
        observer.unobserve(box);
      });
    },
    { threshold: [0, 0.35, 0.75, 1] }
  );

  boxes.forEach((box) => observer.observe(box));
}

export function initInternalLinkTracking() {
  initReadAlsoInviewTracking();
  initPriceSidebarInviewTracking();

  document.addEventListener('click', (event) => {
    const link = event.target?.closest?.('a[data-wla-internal-link="1"]');
    if (!link) return;
    const readAlsoBox = link.closest('[data-wla-read-also="1"]');
    const module = link.dataset.wlaInternalModule || (readAlsoBox ? 'read-also' : 'inline');
    const location = link.dataset.wlaInternalPlacement || (readAlsoBox ? readAlsoBox.dataset.wlaPlacement || 'read-also' : 'article_body');
    const rank = Number(link.dataset.wlaInternalRank || 0);

    const payload = {
      event: 'wla_internal_link_click',
      link_url: link.href,
      link_text: (link.dataset.wlaInternalAnchor || link.textContent || '').trim().slice(0, 120),
      link_location: location,
      link_module: module,
      link_rank: rank || undefined,
      source_url: window.location.href
    };

    if (typeof window.gtag === 'function') {
      window.gtag('event', 'wla_internal_link_click', {
        link_url: payload.link_url,
        link_text: payload.link_text,
        link_location: payload.link_location,
        link_module: payload.link_module,
        link_rank: payload.link_rank,
        source_url: payload.source_url
      });
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);

    if (module === 'article-price-sidebar') {
      const priceSidebarPayload = {
        ...payload,
        event: 'wla_price_sidebar_click'
      };

      if (typeof window.gtag === 'function') {
        window.gtag('event', 'wla_price_sidebar_click', {
          link_url: priceSidebarPayload.link_url,
          link_text: priceSidebarPayload.link_text,
          link_location: priceSidebarPayload.link_location,
          link_rank: priceSidebarPayload.link_rank,
          source_url: priceSidebarPayload.source_url
        });
      }

      window.dataLayer.push(priceSidebarPayload);
    }

    if (module === 'read-also') {
      const readAlsoPayload = {
        ...payload,
        event: 'wla_read_also_click',
        read_also_id: link.dataset.wlaReadAlsoId || readAlsoBox?.dataset.wlaReadAlsoId || ''
      };

      if (typeof window.gtag === 'function') {
        window.gtag('event', 'wla_read_also_click', {
          link_url: readAlsoPayload.link_url,
          link_text: readAlsoPayload.link_text,
          link_location: readAlsoPayload.link_location,
          link_rank: readAlsoPayload.link_rank,
          read_also_id: readAlsoPayload.read_also_id,
          source_url: readAlsoPayload.source_url
        });
      }

      window.dataLayer.push(readAlsoPayload);
      sendReadAlsoEvent('read_also_click', readAlsoBox || link, {
        href: link.href,
        anchor: readAlsoPayload.link_text,
        rank,
        readAlsoId: readAlsoPayload.read_also_id
      });
    }
  });
}

function initPriceSidebarInviewTracking() {
  const sidebars = Array.from(document.querySelectorAll('[data-wla-article-price-sidebar]'));
  if (!sidebars.length || !('IntersectionObserver' in window)) return;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting || entry.intersectionRatio < 0.35) return;

        const payload = {
          event: 'wla_price_sidebar_inview',
          source_url: window.location.href,
          visibility_ratio: Math.round(entry.intersectionRatio * 100) / 100
        };

        if (typeof window.gtag === 'function') {
          window.gtag('event', 'wla_price_sidebar_inview', {
            source_url: payload.source_url,
            visibility_ratio: payload.visibility_ratio
          });
        }

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(payload);
        observer.unobserve(entry.target);
      });
    },
    { threshold: [0, 0.35, 0.75, 1] }
  );

  sidebars.forEach((sidebar) => observer.observe(sidebar));
}
